import { cancel, later } from '@ember/runloop';

/**
 * MAP (in-file navigation graph — keep in sync with the code):
 *   role:      lib — «UI nuova» one-shot motion classes (M19 item 22, owner 2026-10-19: «mini animazioni» when the
 *              state changes). Plays a CSS keyframe once on an element by adding `nv-flash nv-flash--<kind>` and
 *              removing it when the animation ends (or after MOTION_MS when no animation runs: reduced motion,
 *              classic stylesheet, test runner).
 *   exports:   MOTION_KINDS, MOTION_MS, isNuovaDom(), playMotion(element, kind), stopMotion(element)
 *   used by:   modifiers/nv-flash-on-change.js (a value changed), modifiers/nv-enter-on-add.js (a row was added)
 *   css:       app/styles/nuova/_motion.scss (keyframes, ≤ 0.2s, transform / opacity only, reduced motion → none)
 *   gotchas:   nuova only: the classes are added only when <html data-ui='nuova'> (the hook every nuova rule hangs
 *              from), so the classic DOM never changes. The timer is a runloop `later` (cancelled on stop), so
 *              tests settle after it.
 *   tests:     tests/integration/modifiers/nv-flash-on-change-test.js, nv-enter-on-add-test.js
 */
const MOTION_KINDS = ['pop', 'rise', 'fade', 'enter'];

/** The longest keyframe of _motion.scss (0.2s) plus a frame: the class never outlives its animation. */
const MOTION_MS = 240;
const timers = new WeakMap();
function isNuovaDom() {
  return typeof document !== 'undefined' && document.documentElement?.dataset?.ui === 'nuova';
}
function classesOf(kind) {
  return ['nv-flash', `nv-flash--${MOTION_KINDS.includes(kind) ? kind : 'pop'}`];
}

/** Remove every motion class and the pending timer / listener of `element`. */
function stopMotion(element) {
  const pending = timers.get(element);
  if (!pending) return;
  timers.delete(element);
  cancel(pending.timer);
  element.removeEventListener('animationend', pending.done);
  element.classList.remove(...pending.classes);
}

/** Play the `kind` keyframe once on `element` (restarts it when it is still running). No-op outside nuova. */
function playMotion(element, kind = 'pop') {
  if (!element || !isNuovaDom()) return false;
  stopMotion(element);
  const classes = classesOf(kind);
  // a second change while the first animation runs: drop the class, flush the style, add it again
  void element.offsetWidth;
  const done = event => {
    if (event && event.target !== element) return; // a child's own animation bubbling up
    stopMotion(element);
  };
  element.classList.add(...classes);
  element.addEventListener('animationend', done);
  timers.set(element, {
    classes,
    done,
    timer: later(done, MOTION_MS)
  });
  return true;
}

export { MOTION_KINDS, MOTION_MS, isNuovaDom, playMotion, stopMotion };
//# sourceMappingURL=nv-motion.js.map
